import React, { ClipboardEvent, useContext, useEffect, useState } from "react";
import {
  BACKGROUND_GRAY,
  GREEN_05,
  LIGHT_BACKGROUND_GRAY,
} from "../../../constants/css.constants";
import BaseTable from "../../../components/BaseTable/BaseTable";
import ClickableCell from "../../../components/ClickableCell/ClickableCell";
import TableContainer from "../../../components/TableContainer/TableContainer";
import { formatNumbersWithCommas } from "../../../constants/utils";
import { COPY_PASTE_SPLIT } from "../../../constants/app.constants";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";
import {
  AT_ONCE_COLUMNS,
  AtOnceContext,
  createAtOnceDefaultData,
} from "../../../contexts/AtOnceContext";
import { ShipmentHeaderContext } from "../../../contexts/ShipmentHeaderContext";
import useEditRights from "../../../hooks/useEditRights";

const AtOnceTable = () => {
  const { horizontalHeaders, monthsInFirstTwoSeasons } = useContext(
    ShipmentHeaderContext,
  );
  const { atOnceData, setAtOnceData, selectedCell, setNextCell } =
    useContext(AtOnceContext);
  const { canEdit } = useEditRights();
  const { tab } = useParams<{ tab: string }>();
  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname !== currentLocation) {
      setAtOnceData(createAtOnceDefaultData(horizontalHeaders));
      setCurrentLocation(pathname);
    }
  });

  const isNVS = pathname.includes("nvs");
  const [error, setError] = useState(false);
  const [currentLocation, setCurrentLocation] = useState("");

  const updateAtOnce = (row: number, column: number) => (value: string) => {
    const rowToUpdate = atOnceData[row];
    const newRow = [
      ...rowToUpdate.slice(0, column),
      parseInt(value),
      ...rowToUpdate.slice(column + 1),
    ];
    newRow[newRow.length - 1] = newRow
      .slice(0, newRow.length - 1)
      .reduce((acc, curr) => acc + curr, 0);
    const updatedData = [
      ...atOnceData.slice(0, row),
      newRow,
      ...atOnceData.slice(row + 1),
    ];
    if (isNVS) {
      let lRTotals: number[] = [];
      for (let i = 0; i < updatedData.length - 1; i++) {
        for (let j = 0; j < updatedData[i].length; j++) {
          const existingValue = lRTotals[j] || 0;
          lRTotals[j] = existingValue + updatedData[i][j];
        }
      }
      updatedData[atOnceData.length - 1] = lRTotals;
    }
    setAtOnceData(updatedData);
  };
  const onPaste =
    (row: number, column: number) =>
    (e: ClipboardEvent<HTMLTableCellElement>) => {
      const data = e.clipboardData.getData("text");
      const splitData = data
        .split(COPY_PASTE_SPLIT)
        .filter((datum) => datum !== "")
        .map((datum) =>
          datum.split(/\t/).map((datum) => {
            const val = parseInt(datum.replaceAll(",", ""));
            return isNaN(val) ? 0 : val;
          }),
        );
      const dataToUpdate = atOnceData.slice(row, row + splitData.length);
      const differences = splitData.map((dataRow, i) =>
        dataRow.map((value, j) => value - atOnceData[row + i][column + j]),
      );
      const horizontalDifferences = differences.reduce((acc, value) => {
        if (!acc.length) {
          return value;
        }
        return acc.map((val, i) => val + value[i]);
      }, []);

      const verticalDifferences = differences.reduce((acc, value) => {
        const reducedValue = value.reduce(
          (innerAcc, curr) => innerAcc + curr,
          0,
        );
        return acc.concat([reducedValue]);
      }, []);
      const updatedData = dataToUpdate.map((datum, i) => {
        return datum
          .slice(0, column)
          .concat(splitData[i])
          .concat(datum.slice(column + splitData[i].length, datum.length - 1))
          .concat(verticalDifferences[i] + datum[datum.length - 1]);
      });
      if (atOnceData.length > 1) {
        const totalsRow = atOnceData[atOnceData.length - 1];
        const newTotals = horizontalDifferences.map(
          (total, i) => total + totalsRow[i + column],
        );
        const newTotalsRow = totalsRow
          .slice(0, column)
          .concat(newTotals)
          .concat(totalsRow.slice(column + newTotals.length));
        newTotalsRow[newTotalsRow.length - 1] = newTotalsRow
          .slice(0, newTotalsRow.length - 1)
          .reduce((acc, curr) => acc + curr, 0);
        const finalAtOnceData = atOnceData
          .slice(0, row)
          .concat(updatedData)
          .concat(
            atOnceData.slice(row + splitData.length, atOnceData.length - 1),
          )
          .concat([newTotalsRow]);
        setAtOnceData(finalAtOnceData);
      } else {
        setAtOnceData(updatedData);
      }
    };

  const isTB = tab === "tb";
  const isDisabled = isTB || !canEdit;
  const innerCell = (value: number, column: number, row: number) => (
    <ClickableCell
      key={`at-once-${row}-${column}`}
      disabled={isDisabled || (isNVS && row === atOnceData.length - 1)}
      updateData={updateAtOnce(row, column)}
      transform={(value) => formatNumbersWithCommas(value)}
      value={value}
      onPaste={onPaste(row, column)}
      isSelected={selectedCell.row === row && selectedCell.column === column}
      setNextCell={setNextCell(row, column)}
      styles={
        isDisabled && column < monthsInFirstTwoSeasons
          ? { backgroundColor: LIGHT_BACKGROUND_GRAY, color: "#000" }
          : isDisabled || (isNVS && row === atOnceData.length - 1)
            ? { backgroundColor: BACKGROUND_GRAY, cursor: "default" }
            : column < monthsInFirstTwoSeasons
              ? { backgroundColor: GREEN_05, color: "black" }
              : { backgroundColor: "#fff", color: "black" }
      }
      type={"number"}
    />
  );

  return (
    <>
      <TableContainer label={"At Once"}>
        <BaseTable
          error={error}
          data={atOnceData}
          horizontalHeaders={[...horizontalHeaders.MONTHS, "Total"]}
          tab={tab.toUpperCase()}
          verticalHeaders={
            isNVS ? AT_ONCE_COLUMNS : AT_ONCE_COLUMNS.slice(0, 1)
          }
          innerCell={innerCell}
        />
      </TableContainer>
    </>
  );
};

export default AtOnceTable;
